import { LayerDto, LayerType } from '../models/layer/layer.dto';
import merge from 'lodash/merge';
import { CreativeUnitDto, CreativeUnitWithCustomizationsDto } from '../models/creative-unit/creative-unit.dto';
import { Evaluate } from '../../_core/utils/utils.evaluate';
import { MergeTags } from '../../_core/utils/utils.merge-tags';
import { NumberUtils } from '../../_core/utils/utils.number';
import { LayoutMode } from '../models/layer/layout-group.dto';

interface LayerBounds {
	left: number;
	top: number;
	width: number;
	height: number;
	right: number;
	bottom: number;
}

export const DEFAULT_LAYER_Z_INDEX = 50;

export class LayersUtils {
	public static readonly GRID_LAYER_ID = '__GRID_LAYER';
	/**
	 * Flatten the layers of a creative unit into a single array.
	 */
	public static flattenLayersFromCreativeUnit(obj: CreativeUnitDto | LayerDto): LayerDto[] {
		// Get all layers of an creativeUnit, and sub layers of any groups.  Flatten all into a single array.
		let layers: LayerDto[] = [];

		// Add the layers from the object.
		if (obj.layers) {
			layers = layers.concat(obj.layers);
		}

		// If this is a frame, we need to flatten the sub layers.
		for (let layer of layers) {
			if (layer.type === LayerType.GROUP) {
				layers = layers.concat(this.flattenLayersFromCreativeUnit(layer));
			}
		}

		return layers;
	}

	/**
	 * Recursively flatten the layers of an array of layers into a single array.
	 */
	public static flattenLayers<T extends LayerDto = LayerDto>(layers: LayerDto[]): T[] {
		return layers?.reduce<T[]>((acc, layer) => {
			acc.push(layer as T);

			if (layer.type === LayerType.GROUP && layer.layers?.length) {
				acc = acc.concat(this.flattenLayers(layer.layers));
			}

			return acc;
		}, [] as T[]);
	}

	/**
	 * Recursively map all layers of an array of layers with a mapping function.
	 */
	public static mapAllLayers(layers: LayerDto[], func: Function, parentId?: string) {
		return (layers || [])?.map(layer => {
			const newLayer = { ...func(layer, parentId) };

			if (newLayer.layers) {
				newLayer.layers = this.mapAllLayers(newLayer.layers, func, newLayer.id);
			}

			return newLayer;
		});
	}

	/**
	 * Recursively map all layers of an array of layers with a mapping function, adjusting the ID based on nesting as we go.
	 */
	public static mapAllLayersWithIdNesting(layers: LayerDto[], func: Function, id?: string) {
		if (!id) {
			id = new Date().getTime().toString();
		}

		return (layers || [])?.map((layer, index) => {
			const layerId = id + (index + 1);

			const newLayer = {
				...func(layer),
				id: layerId
			};

			if (newLayer.layers) {
				newLayer.layers = this.mapAllLayersWithIdNesting(newLayer.layers, func, layerId);
			}

			return newLayer;
		});
	}

	/**
	 * Recursively find the first visible layer that matches a condition.
	 * NOTE: Returns the first layer, not a boolean.
	 */
	public static findFirstVisibleLayer(layers: LayerDto[], func: Function): LayerDto {
		for (const layer of layers) {
			if (!layer.hidden) {
				if (func(layer)) {
					return layer;
				}
				if (layer.layers) {
					const subLayer = this.findFirstVisibleLayer(layer.layers, func);

					if (subLayer) {
						return subLayer;
					}
				}
			}
		}
		return null;
	}

	/**
	 * Recursively find all layers of an array of layers that match a condition.
	 * NOTE: Returns the list of found arrays, not a boolean.
	 */
	public static findAllLayers(layers: LayerDto[], func: Function): LayerDto[] {
		const result = (layers || []).reduce<LayerDto[]>((acc, layer) => {
			if (func(layer)) {
				acc.push(layer);
			}

			if (layer.layers) {
				acc = acc.concat(this.findAllLayers(layer.layers, func));
			}

			return acc;
		}, []);

		return result;
	}

	/**
	 * Recursively filter all layers of an array of layers that match a condition.
	 */
	public static filterAllLayers(layers: LayerDto[], func: Function, flatten?: boolean, filterGroups?: boolean) {
		let filteredLayers: LayerDto[] = [];

		(layers || []).forEach(layer => {
			// console.log('Filter Checking', layer.label, func(layer));
			const shouldKeep = func(layer);

			if (shouldKeep || (filterGroups && layer.layers)) {
				if (layer.layers) {
					if (flatten) {
						filteredLayers = filteredLayers.concat(this.filterAllLayers(layer.layers, func));
					} else {
						let newLayer = {
							...layer,
							layers: this.filterAllLayers(layer.layers, func)
						};
						if (!filterGroups || newLayer.layers.length > 0) {
							filteredLayers.push(newLayer);
						}
					}
				} else {
					filteredLayers.push(layer);
				}
			}
		});

		return filteredLayers;
	}

	/**
	 * Recursively find the first layer of each layer group (including the group itself) that matches a condition.
	 * NOTE: Returns the list of found arrays, not a boolean.
	 */
	public static findFirstLayers<T extends LayerDto = LayerDto>(layers: LayerDto[], func: Function): T[] {
		return (layers || []).reduce<T[]>((acc, layer) => {
			if (func(layer)) {
				acc.push(layer as T);
			} else {
				if (layer.layers) {
					acc = acc.concat(this.findFirstLayers(layer.layers, func));
				}
			}

			return acc;
		}, [] as T[]);
	}

	/**
	 * Recursively find the first layer of each layer group (including the group itself) that matches a condition, otherwise take the first.
	 * NOTE: Returns the list of found arrays, not a boolean.
	 */
	public static findFirstLayersOrFallbackToStart<T extends LayerDto = LayerDto>(layers: LayerDto[], func: Function): T[] {
		const results: T[] = [];

		for (const layer of layers) {
			let subtreeMatches: T[] = [];

			// If this node itself matches, take it (don't descend)
			if (func(layer)) {
				subtreeMatches.push(layer as T);
			} else if (layer.layers?.length) {
				// Otherwise try to find matches in children
				subtreeMatches = this.findFirstLayersOrFallbackToStart(layer.layers, func);
			}

			if (subtreeMatches.length > 0) {
				results.push(...subtreeMatches);
			} else {
				// Nothing matched inside this subtree -> fall back to the first element of this subtree (the root)
				results.push(layer as T);
			}
		}

		return results;
	}

	/**
	 * Find the first layer that matches a condition.
	 * NOTE: Returns the found layer, not a boolean.
	 */
	public static findLayer(layers: LayerDto[], func: Function): LayerDto {
		for (const layer of layers) {
			if (func(layer)) {
				return layer;
			}
			if (layer.layers) {
				const found = this.findLayer(layer.layers, func);
				if (found) {
					return found;
				}
			}
		}
		return null;
	}

	/**
	 * Shorthand to find a layer by its ID.
	 * @param layers
	 * @param id
	 */
	public static findLayerById(layers: LayerDto[], id: string): LayerDto | null {
		return this.findLayer(layers, (layer: LayerDto) => layer.id === id);
	}

	/**
	 * Recursively override all of the layers of a package with the layers of a unit.
	 */
	public static overrideLayers(packageLayers: LayerDto[], unitLayers: LayerDto[]): LayerDto[] {
		return this.mapAllLayers(packageLayers, (layer: LayerDto) => {
			const layerOverrides = this.findAllLayers(
				unitLayers as LayerDto[],
				(creativeUnitLayer: LayerDto) => creativeUnitLayer.id === layer.id
			);

			if (layerOverrides.length) {
				// return merge(plainToClass(LayerDto, layerOverrides[0]), JSON.parse(JSON.stringify(layer)));
				return merge(layerOverrides[0], JSON.parse(JSON.stringify(layer)));
			}

			return layer;
		});
	}

	/**
	 * Recursively find the parent id for a given layer id within a layer tree.
	 */
	public static findParentId(layers: LayerDto[], id: string, parentId?: string): string | null {
		for (const layer of layers || []) {
			if (layer.id === id) {
				return parentId || null;
			}

			if (layer.layers?.length) {
				const result = this.findParentId(layer.layers, id, layer.id);
				if (result !== null) {
					return result;
				}
			}
		}

		return null;
	}

	/**
	 * Recursively reduce all layers of an array of layers with a reducing function.
	 */
	public static reduceAllLayers(layers: LayerDto[], func: Function) {
		return layers.reduce((acc, layer) => {
			const newLayer = { ...func(layer) };

			if (newLayer.layers) {
				newLayer.layers = this.reduceAllLayers(newLayer.layers, func);
			}

			return acc.concat(newLayer);
		}, []);
	}

	public static findAllVisibleLayers(layers: LayerDto[], state: any): LayerDto[] {
		return this.filterAllLayers(layers, layer => {
			// console.log('Checking Visibility', layer.label, layer.visibilityConditions);

			// check if the layer is hidden
			if (layer.hidden === true) {
				return false;
			}

			if (layer.visibilityConditions) {
				let visible = true;

				// If visibilityConditions are not an array, wrap into an array.
				let conditions = layer.visibilityConditions;
				if (!Array.isArray(conditions)) {
					conditions = [conditions];
				}

				// Iterate over the visibility conditions and check if they are valid.
				for (let condition of conditions) {
					// console.log('Checking visibility conditions', condition, CollectionUtils.isConditionValid(condition, state), layer.visibilityConditions);

					// Apply merge tags to the condition.
					try {
						condition = JSON.parse(MergeTags.applyMergeTagsToString(JSON.stringify(condition), state));
						// console.log('Successful merge tags', condition);
					} catch (e) {
						console.warn('Unable to parse condition merge tags');
					}

					// Evaluate the condition.
					if (!Evaluate.evaluate(condition, state)) {
						visible = false;
						break;
					}
				}
				return visible;
			} else {
				return true;
			}
		});
	}

	/**
	 * Finds the parent layer of a given layer by its ID.
	 * @param layers
	 * @param layerId
	 * @param parent
	 */
	public static findLayerParent(layers: LayerDto[], layerId: string, parent?: LayerDto): LayerDto | undefined {
		return (layers || [])
			.map(layer =>
				layer.id === layerId ? parent : layer.layers?.length ? LayersUtils.findLayerParent(layer.layers, layerId, layer) : undefined
			)
			.find(result => result?.id !== undefined);
	}

	/**
	 * Sets the visibility of a layer by its ID.
	 * @param layers
	 * @param layerId
	 * @param hidden
	 */
	public static setVisibilityForLayer(layers: LayerDto[], layerId: string, hidden: boolean): LayerDto[] {
		return this.mapAllLayers(layers, (layer: LayerDto) => {
			if (layer.id === layerId) {
				return {
					...layer,
					hidden: hidden
				};
			}
			return layer;
		});
	}

	public static adjustAnimationDuration(oldTotalDuration: number, newTotalDuration: number, affectedLayers: LayerDto[]): LayerDto[] {
		const newDurationIsLessThanOldDuration = oldTotalDuration > newTotalDuration;
		const correctionRatio = newTotalDuration / oldTotalDuration;

		const result = affectedLayers.map(layer => {
			if (layer.timing == null) return layer;

			let timing = {
				...this.applyStartOffsetCorrection(layer, correctionRatio),
				...this.applyTotalDurationCorrection(layer, oldTotalDuration, newTotalDuration, correctionRatio)
			};

			if (newDurationIsLessThanOldDuration) timing = { ...timing, ...this.applyAnimationTimingCorrections(layer, correctionRatio) };

			return {
				...layer,
				timing: {
					...layer.timing,
					...timing
				}
			};
		});

		return result;
	}

	private static applyStartOffsetCorrection(layer: LayerDto, correctionRatio: number) {
		const result = {};
		const layerStartOffset = Number(layer.timing.startOffset);
		const layerStartOffsetIsValid = layerStartOffset != null && !isNaN(layerStartOffset);

		if (layerStartOffsetIsValid && layerStartOffset > 0) {
			result['startOffset'] = layerStartOffset * correctionRatio;
		}

		return result;
	}

	private static applyTotalDurationCorrection(
		layer: LayerDto,
		oldTotalDuration: number,
		newTotalDuration: number,
		correctionRatio: number
	) {
		const result = {};
		const layerDuration = Number(layer.timing.duration);
		const layerDurationIsValid = layerDuration != null && !isNaN(layerDuration);

		if (layerDurationIsValid && layerDuration === oldTotalDuration) {
			result['duration'] = newTotalDuration;
		} else if (layerDurationIsValid && layerDuration < oldTotalDuration) {
			result['duration'] = layerDuration * correctionRatio;
		}

		return result;
	}

	private static applyAnimationTimingCorrections(layer: LayerDto, correctionRatio: number) {
		const result = {};

		if (layer.timing.in != null) {
			let newInAnimation = {
				...layer.timing.in,
				delay: layer.timing.in.delay * correctionRatio,
				duration: layer.timing.in.duration * correctionRatio
			};

			result['in'] = newInAnimation;
		}

		if (layer.timing.out != null) {
			let newOutAnimation = {
				...layer.timing.out,
				delay: layer.timing.out.delay * correctionRatio,
				duration: layer.timing.out.duration * correctionRatio
			};

			result['out'] = newOutAnimation;
		}

		return result;
	}

	/**
	 * Generate the next group label based on existing group labels in the layers.
	 * @param layers The array of layers to check for existing group labels.
	 * @param base The base string for the group label. Default is "Group".
	 * @returns The next group label in the format "Group X", where X is the next available number.
	 */
	public static nextGroupLabel(layers: LayerDto[], base = 'Group'): string {
		const flat = LayersUtils.flattenLayers(layers) as LayerDto[];

		const groups = flat.filter(l => l?.type === LayerType.GROUP);

		const re = new RegExp(`^${base}\\s*(\\d+)$`, 'i');

		let maxNum = 0;

		for (const g of groups) {
			const text = (g.label ?? '').trim();
			const m = re.exec(text);
			if (m) {
				const n = parseInt(m[1], 10);
				if (!Number.isNaN(n) && n > maxNum) maxNum = n;
			}
		}

		return `${base} ${maxNum + 1}`;
	}

	/**
	 * Get the actual rendered dimensions of a layer in pixels.
	 * Recursively resolves relative units (%, cqw, cqh, etc.) by walking up the parent chain.
	 * @param layerId The ID of the layer to get dimensions for
	 * @param layers The layer tree to search within
	 * @param rootDimensions The dimensions of the root container (creative unit) in pixels
	 * @returns Object with width and height in pixels
	 */
	public static getElementDimensions(
		layerId: string,
		layers: LayerDto[],
		rootDimensions: { width: number; height: number }
	): { width: number; height: number } {
		// Find the target layer and build parent chain
		const layerChain: LayerDto[] = [];
		const findLayerChain = (layers: LayerDto[], targetId: string, chain: LayerDto[]): boolean => {
			for (const layer of layers) {
				if (layer.id === targetId) {
					layerChain.push(...chain, layer);
					return true;
				}
				if (layer.layers) {
					if (findLayerChain(layer.layers, targetId, [...chain, layer])) {
						return true;
					}
				}
			}
			return false;
		};

		findLayerChain(layers, layerId, []);

		if (layerChain.length === 0) {
			// Layer not found, return root dimensions
			return rootDimensions;
		}

		// Calculate dimensions by walking through the chain from root to target
		let currentDimensions = { ...rootDimensions };

		for (const layer of layerChain) {
			const width = this.parseDimensionValue(layer.style?.width, currentDimensions.width);
			const height = this.parseDimensionValue(layer.style?.height, currentDimensions.height);
			currentDimensions = { width, height };
		}

		return currentDimensions;
	}

	/**
	 * Parse a CSS dimension value and convert it to pixels based on parent size.
	 * Handles: px, %, cqw, cqh, vw, vh, and raw numbers
	 */
	private static parseDimensionValue(value: string | number | undefined, parentSize: number): number {
		if (value === undefined || value === null) {
			return parentSize;
		}

		// If already a number, return it
		if (typeof value === 'number') {
			return value;
		}

		// Convert to string for parsing
		const strValue = String(value).trim();

		// Extract numeric value and unit
		const match = strValue.match(/^([-+]?[\d.]+)(.*)$/);
		if (!match) {
			return parentSize;
		}

		const numericValue = parseFloat(match[1]);
		const unit = match[2].trim().toLowerCase();

		if (isNaN(numericValue)) {
			return parentSize;
		}

		// Handle different units
		switch (unit) {
			case '%':
			case 'cqw': // Container query width
			case 'cqh': // Container query height
			case 'vw': // Viewport width
			case 'vh': // Viewport height
				// All these are percentage-based relative to parent/container
				return (numericValue / 100) * parentSize;

			case 'px':
			case '': // No unit means pixels
				return numericValue;

			default:
				// Unknown unit, default to parent size
				return parentSize;
		}
	}

	public static removeLayerById(layers: LayerDto[], idToRemove: string): LayerDto[] {
		return (
			layers
				// remove layers that match the id
				.filter(layer => layer.id !== idToRemove)
				// recursively process children
				.map(layer => {
					if (layer.layers && layer.layers.length) {
						const updatedChildren = this.removeLayerById(layer.layers, idToRemove);
						return { ...layer, layers: updatedChildren };
					}
					return layer;
				})
		);
	}

	public static getLayersInGroupWithStyles(
		layers: LayerDto[],
		containerDimensions: { width: number; height: number },
		groupLayer: LayerDto,
		creativeUnitId?: string,
		isNested: boolean = false
	) {
		// Get rotated bounds for each layer (for group bounding box calculation)
		const rotatedBounds = layers.map(layer =>
			LayersUtils.getLayerBoundsFromStyle(layer, containerDimensions, { includeRotation: true })
		);

		// Get unrotated bounds for each layer (for child CSS positioning)
		const unrotatedBounds = layers.map(layer =>
			LayersUtils.getLayerBoundsFromStyle(layer, containerDimensions, { includeRotation: false })
		);

		// Calculate combined group bounding box using ROTATED bounds
		const groupBounds = rotatedBounds.reduce((acc, bounds) => LayersUtils.combineBounds(acc, bounds));

		const groupWidth = groupBounds.width || 1;
		const groupHeight = groupBounds.height || 1;

		// Group should use the highest z-index of its children.
		let highestZIndex: number | null = null;

		// Helper to extract rotation from transform
		const extractRotation = (style: LayerDto['style']): string | undefined => {
			if (!style?.transform) return undefined;
			const transformStr = typeof style.transform === 'string' ? style.transform : '';
			const rotateMatch = transformStr.match(/rotate\([^)]+\)/i);
			return rotateMatch ? rotateMatch[0] : undefined;
		};

		// Map each layer to its group-relative positioned version
		const layersForGroup = layers.map((layer, i) => {
			const layerZIndex = layer?.style?.zIndex ?? null;

			if (layerZIndex !== null && !isNaN(layerZIndex)) {
				if (highestZIndex === null || layerZIndex > highestZIndex) {
					highestZIndex = layerZIndex;
				}
			}

			const rotation = extractRotation(layer.style);

			// visual center of the element on canvas (unrotated)
			const elementCenterX = unrotatedBounds[i].left + unrotatedBounds[i].width / 2;
			const elementCenterY = unrotatedBounds[i].top + unrotatedBounds[i].height / 2;

			// group's top-left origin
			const groupOriginX = groupBounds.left;
			const groupOriginY = groupBounds.top;

			// left/top in CSS = center - halfSize
			const relativeLeft = elementCenterX - groupOriginX - unrotatedBounds[i].width / 2;
			const relativeTop = elementCenterY - groupOriginY - unrotatedBounds[i].height / 2;

			const leftCqw = `${(relativeLeft / groupWidth) * 100}cqw`;
			const topCqh = `${(relativeTop / groupHeight) * 100}cqh`;
			const translatePart = `translate(${leftCqw}, ${topCqh})`;
			const transformValue = rotation ? `${translatePart} ${rotation}` : translatePart;

			const layerForGroup = {
				...layer,
				creativeUnitId: layer.creativeUnitId,
				style: {
					...layer.style,
					position: 'absolute',
					left: '0',
					top: '0',
					width: `${(unrotatedBounds[i].width / groupWidth) * 100}cqw`,
					height: `${(unrotatedBounds[i].height / groupHeight) * 100}cqh`,
					transform: transformValue
				}
			};

			return layerForGroup;
		});

		const groupLeftCqw = NumberUtils.toPercentage(groupBounds.left, containerDimensions.width || 1, 4);
		const groupTopCqh = NumberUtils.toPercentage(groupBounds.top, containerDimensions.height || 1, 4);

		groupLayer = {
			...groupLayer,
			layers: layersForGroup,
			style: {
				...groupLayer.style,
				left: '0px',
				top: '0px',
				width: `${NumberUtils.toPercentage(groupWidth, containerDimensions.width || 1, 4)}cqw`,
				height: `${NumberUtils.toPercentage(groupHeight, containerDimensions.height || 1, 4)}cqh`,
				transform: `translate(${groupLeftCqw}cqw, ${groupTopCqh}cqh)`,
				zIndex: highestZIndex !== null ? highestZIndex : DEFAULT_LAYER_Z_INDEX
			},
			creativeUnitId
		};
		if (isNested) {
			groupLayer = this.formatGroupPositionForNested(groupLayer, containerDimensions);
		}

		return groupLayer;
	}

	private static formatGroupPositionForNested(group: LayerDto, containerDimensions: { width: number; height: number }): LayerDto {
		const pw = containerDimensions.width || 1;
		const ph = containerDimensions.height || 1;

		let gl = 0;
		let gt = 0;
		const translateMatch = (group.style.transform || '').match(/translate\(\s*([-.0-9]+)px\s*,\s*([-.0-9]+)px\s*\)/);
		if (translateMatch) {
			gl = parseFloat(translateMatch[1]) || 0;
			gt = parseFloat(translateMatch[2]) || 0;
		}

		const gw = parseFloat(group.style.width) || 0;
		const gh = parseFloat(group.style.height) || 0;

		const leftCqw = `${(gl / pw) * 100}cqw`;
		const topCqh = `${(gt / ph) * 100}cqh`;

		return {
			...group,
			style: {
				...group.style,
				left: '0',
				top: '0',
				width: `${(gw / pw) * 100}cqw`,
				height: `${(gh / ph) * 100}cqh`,
				transform: `translate(${leftCqw}, ${topCqh})`
			}
		};
	}

	public static getLayersUngroupedWithStyles(
		allLayers: LayerDto[],
		groupLayer: LayerDto,
		rootContainerDimensions: { width: number; height: number }
	): LayerDto[] {
		const children = groupLayer.layers || [];
		if (!children.length) return [];

		// Get parent dimensions for nested groups
		const parentContainerDimensions = this.resolveParentContainerDimensions(allLayers, groupLayer.id, rootContainerDimensions);

		// Get the group's unrotated bounds in the parent container
		const groupBounds = this.getLayerBoundsFromStyle(groupLayer, parentContainerDimensions, { includeRotation: false });
		const groupWidth = groupBounds.width || 1;
		const groupHeight = groupBounds.height || 1;

		const contentWidth = Math.max(groupWidth, 1);
		const contentHeight = Math.max(groupHeight, 1);

		// Content box origin in parent container coordinates
		const contentOriginX = groupBounds.left;
		const contentOriginY = groupBounds.top;

		// Helper to extract rotation from transform
		const extractRotation = (style: LayerDto['style']): string | undefined => {
			if (!style?.transform) return undefined;
			const transformStr = typeof style.transform === 'string' ? style.transform : '';
			const rotateMatch = transformStr.match(/rotate\([^)]+\)/i);
			return rotateMatch ? rotateMatch[0] : undefined;
		};

		const parentId = this.findParentId(allLayers, groupLayer.id);
		const isNestedInGroup = !!parentId;

		return children.map(child => {
			const childBounds = this.getLayerBoundsFromStyle(
				child,
				{ width: contentWidth, height: contentHeight },
				{ includeRotation: false }
			);

			const rotation = extractRotation(child.style);

			const absoluteLeft = contentOriginX + childBounds.left;
			const absoluteTop = contentOriginY + childBounds.top;

			let leftVal: string;
			let topVal: string;
			let widthVal: string;
			let heightVal: string;
			let transformVal: string;

			if (isNestedInGroup) {
				const parentW = parentContainerDimensions.width || 1;
				const parentH = parentContainerDimensions.height || 1;
				leftVal = `${(absoluteLeft / parentW) * 100}cqw`;
				topVal = `${(absoluteTop / parentH) * 100}cqh`;
				widthVal = `${(childBounds.width / parentW) * 100}cqw`;
				heightVal = `${(childBounds.height / parentH) * 100}cqh`;
				transformVal = rotation;
			} else {
				leftVal = '0px';
				topVal = '0px';
				widthVal = `${NumberUtils.toPercentage(childBounds.width, rootContainerDimensions.width, 4)}cqw`;
				heightVal = `${NumberUtils.toPercentage(childBounds.height, rootContainerDimensions.height, 4)}cqh`;

				const groupLeftVal = NumberUtils.toPercentage(absoluteLeft, rootContainerDimensions.width, 4);
				const groupTopVal = NumberUtils.toPercentage(absoluteTop, rootContainerDimensions.height, 4);

				const translatePart = `translate(${groupLeftVal}cqw, ${groupTopVal}cqh)`;
				transformVal = rotation ? `${translatePart} ${rotation}` : translatePart;
			}

			const ungroupedStyle: Record<string, any> = {
				...child.style,
				position: 'absolute',
				left: leftVal,
				top: topVal,
				width: widthVal,
				height: heightVal
			};

			if (transformVal) {
				ungroupedStyle.transform = transformVal;
			} else {
				delete ungroupedStyle.transform;
			}

			return { ...child, style: ungroupedStyle };
		});
	}

	/**
	 * Walk up the layer tree from a given layer to compute the
	 * dimensions of its immediate parent container.
	 */
	public static resolveParentContainerDimensions(
		allLayers: LayerDto[],
		layerId: string,
		rootContainerDimensions: { width: number; height: number }
	): { width: number; height: number } {
		const parentId = this.findParentId(allLayers, layerId);
		if (!parentId) {
			// Layer is at root level — its container is the creative unit / canvas
			return rootContainerDimensions;
		}

		const parentLayer = this.findLayer(allLayers, (l: LayerDto) => l.id === parentId);
		if (!parentLayer) return rootContainerDimensions;

		const grandparentDimensions = this.resolveParentContainerDimensions(allLayers, parentId, rootContainerDimensions);

		// Get the parent layer's bounds in its own container
		const parentBounds = this.getLayerBoundsFromStyle(parentLayer, grandparentDimensions, { includeRotation: false });

		return {
			width: Math.max(parentBounds.width, 1),
			height: Math.max(parentBounds.height, 1)
		};
	}

	public static filterGroupLayers(layers: LayerDto[]): LayerDto[] {
		// Collect all IDs that are children of any group
		const groupIds = new Set<string>();

		const collectGroupIds = (items: LayerDto[]) => {
			for (const item of items) {
				if (item.type === 'group' && Array.isArray(item.layers)) {
					groupIds.add(item.id);
				}
			}
		};
		collectGroupIds(layers);
		// Return only items that are of type group
		return layers.filter(layer => groupIds.has(layer.id));
	}

	/**
	 * Calculate the combined bounding box of two bounds
	 */
	private static combineBounds(boundsA: LayerBounds, boundsB: LayerBounds): LayerBounds {
		const left = Math.min(boundsA.left, boundsB.left);
		const top = Math.min(boundsA.top, boundsB.top);
		const right = Math.max(boundsA.right, boundsB.right);
		const bottom = Math.max(boundsA.bottom, boundsB.bottom);

		return {
			left,
			top,
			width: right - left,
			height: bottom - top,
			right,
			bottom
		};
	}

	/**
	 * Extract bounds from a layer's style, converting cqw/cqh to pixels using container dimensions.
	 * Also handles transform translations and rotation - which is often where position is stored!
	 *
	 * @param layer The layer to get bounds for
	 * @param containerDimensions The container dimensions for converting relative units
	 * @param options.includeRotation If true (default), returns the axis-aligned bounding box that
	 *        encompasses the rotated element. If false, returns the unrotated bounds (CSS position).
	 */
	private static getLayerBoundsFromStyle(
		layer: LayerDto,
		containerDimensions: { width: number; height: number },
		options: { includeRotation?: boolean } = {}
	): { left: number; top: number; width: number; height: number; right: number; bottom: number } {
		const { includeRotation = true } = options;
		const style = layer.style || {};
		const containerWidth = containerDimensions.width;
		const containerHeight = containerDimensions.height;

		let left = this.parseStyleValueWithUnit(style.left, containerWidth, containerHeight, 'horizontal');
		let top = this.parseStyleValueWithUnit(style.top, containerWidth, containerHeight, 'vertical');
		let width = this.parseStyleValueWithUnit(style.width, containerWidth, containerHeight, 'horizontal');
		let height = this.parseStyleValueWithUnit(style.height, containerWidth, containerHeight, 'vertical');
		let rotationDeg = 0;

		if (style.transform) {
			const transform = style.transform;

			if (typeof transform === 'string') {
				// Parse transform string - handles both translate(x,y) and translateX/translateY
				const { translateX, translateY, rotation } = this.parseTransformValues(transform, containerWidth, containerHeight);
				left += translateX;
				top += translateY;
				rotationDeg = rotation;
			} else if (typeof transform === 'object') {
				// Handle transform as object like { translateX: '100px', translateY: '50px' }
				const transformObj = transform as Record<string, unknown>;
				if (transformObj.translateX !== undefined) {
					left += this.parseStyleValueWithUnit(transformObj.translateX as string, containerWidth, containerHeight, 'horizontal');
				}
				if (transformObj.translateY !== undefined) {
					top += this.parseStyleValueWithUnit(transformObj.translateY as string, containerWidth, containerHeight, 'vertical');
				}
				if (transformObj.rotate !== undefined) {
					const rotateStr = String(transformObj.rotate);
					const rotateMatch = rotateStr.match(/(-?[\d.]+)/);
					if (rotateMatch) {
						rotationDeg = parseFloat(rotateMatch[1]);
					}
				}
			}
		}

		// Check for alternative dimension properties as fallbacks
		if (width === 0) {
			if (style.minWidth) {
				width = this.parseStyleValueWithUnit(style.minWidth, containerWidth, containerHeight, 'horizontal');
			} else if (style.maxWidth) {
				width = this.parseStyleValueWithUnit(style.maxWidth, containerWidth, containerHeight, 'horizontal');
			}
		}
		if (height === 0) {
			if (style.minHeight) {
				height = this.parseStyleValueWithUnit(style.minHeight, containerWidth, containerHeight, 'vertical');
			} else if (style.maxHeight) {
				height = this.parseStyleValueWithUnit(style.maxHeight, containerWidth, containerHeight, 'vertical');
			}
		}

		// This prevents the group from collapsing to zero size
		if (width === 0) {
			width = 100;
		}
		if (height === 0) {
			height = 50;
		}

		// If there's rotation AND we want to include it, calculate the rotated bounding box
		// This matches what getBoundingClientRect() returns for rotated elements
		if (includeRotation && rotationDeg !== 0) {
			const rotatedBounds = this.calculateRotatedBounds(left, top, width, height, rotationDeg);
			return rotatedBounds;
		}

		return {
			left,
			top,
			width,
			height,
			right: left + width,
			bottom: top + height
		};
	}

	/**
	 * Calculate the axis-aligned bounding box of a rotated rectangle.
	 * This matches what getBoundingClientRect() returns for a rotated element.
	 *
	 * @param left Original left position
	 * @param top Original top position
	 * @param width Original width
	 * @param height Original height
	 * @param rotationDeg Rotation in degrees
	 */
	private static calculateRotatedBounds(
		left: number,
		top: number,
		width: number,
		height: number,
		rotationDeg: number
	): { left: number; top: number; width: number; height: number; right: number; bottom: number } {
		// Convert to radians
		const rad = (rotationDeg * Math.PI) / 180;
		const cos = Math.abs(Math.cos(rad));
		const sin = Math.abs(Math.sin(rad));

		// Calculate center of original rectangle
		const centerX = left + width / 2;
		const centerY = top + height / 2;

		// Calculate new dimensions after rotation
		// The bounding box of a rotated rectangle is:
		const newWidth = width * cos + height * sin;
		const newHeight = width * sin + height * cos;

		// Calculate new top-left based on center (rotation happens around center)
		const newLeft = centerX - newWidth / 2;
		const newTop = centerY - newHeight / 2;

		return {
			left: newLeft,
			top: newTop,
			width: newWidth,
			height: newHeight,
			right: newLeft + newWidth,
			bottom: newTop + newHeight
		};
	}

	/**
	 * Parse transform values from a transform string.
	 * Handles multiple formats:
	 * - translate(x, y) - combined
	 * - translateX(x) translateY(y) - separate
	 * - translate(x) - single value (applies to X only)
	 * - rotate(deg) - rotation
	 */
	private static parseTransformValues(
		transform: string,
		containerWidth: number,
		containerHeight: number
	): { translateX: number; translateY: number; rotation: number } {
		let translateX = 0;
		let translateY = 0;
		let rotation = 0;

		// First try to match translate(x, y) or translate(x)
		const translateMatch = transform.match(/translate\(([^)]+)\)/i);
		if (translateMatch) {
			const values = translateMatch[1].split(',').map(v => v.trim());
			if (values.length >= 1) {
				translateX = this.parseStyleValueWithUnit(values[0], containerWidth, containerHeight, 'horizontal');
			}
			if (values.length >= 2) {
				translateY = this.parseStyleValueWithUnit(values[1], containerWidth, containerHeight, 'vertical');
			}
		}

		// Also check for separate translateX/translateY (they add to translate if both exist)
		const translateXMatch = transform.match(/translateX\(([^)]+)\)/i);
		if (translateXMatch) {
			translateX += this.parseStyleValueWithUnit(translateXMatch[1].trim(), containerWidth, containerHeight, 'horizontal');
		}

		const translateYMatch = transform.match(/translateY\(([^)]+)\)/i);
		if (translateYMatch) {
			translateY += this.parseStyleValueWithUnit(translateYMatch[1].trim(), containerWidth, containerHeight, 'vertical');
		}

		// Parse rotation - handles rotate(Xdeg), rotate(Xrad), rotate(Xturn)
		const rotateMatch = transform.match(/rotate\(([^)]+)\)/i);
		if (rotateMatch) {
			const rotateValue = rotateMatch[1].trim();
			// Extract numeric value
			const numMatch = rotateValue.match(/(-?[\d.]+)/);
			if (numMatch) {
				let rotationValue = parseFloat(numMatch[1]);
				// Convert to degrees if needed
				if (rotateValue.includes('rad')) {
					rotationValue = (rotationValue * 180) / Math.PI;
				} else if (rotateValue.includes('turn')) {
					rotationValue = rotationValue * 360;
				}
				// Default is degrees (deg or no unit)
				rotation = rotationValue;
			}
		}

		return { translateX, translateY, rotation };
	}

	/**
	 * Parse a CSS value string with unit and convert to pixels.
	 */
	private static parseStyleValueWithUnit(
		value: string | number | undefined,
		containerWidth: number,
		containerHeight: number,
		axis: 'horizontal' | 'vertical'
	): number {
		if (value === undefined || value === null) return 0;
		if (typeof value === 'number') return value;

		const numericValue = parseFloat(value);
		if (isNaN(numericValue)) return 0;

		const lowerValue = value.toLowerCase();
		if (lowerValue.endsWith('cqw')) {
			return (numericValue / 100) * containerWidth;
		} else if (lowerValue.endsWith('cqh')) {
			return (numericValue / 100) * containerHeight;
		} else if (lowerValue.endsWith('cqmax')) {
			return (numericValue / 100) * Math.max(containerWidth, containerHeight);
		} else if (lowerValue.endsWith('cqmin')) {
			return (numericValue / 100) * Math.min(containerWidth, containerHeight);
		} else if (lowerValue.endsWith('%')) {
			return (numericValue / 100) * (axis === 'horizontal' ? containerWidth : containerHeight);
		}
		// Default to pixels
		return numericValue;
	}

	public static filterTopLevelLayers(layers: any[]): any[] {
		// Collect all IDs that are children of any group
		const childIds = new Set<string>();

		const collectChildIds = (items: any[]) => {
			for (const item of items) {
				if (item.type === 'group' && Array.isArray(item.layers)) {
					for (const child of item.layers) {
						childIds.add(child.id);
					}
					// Recurse in case groups are nested
					collectChildIds(item.layers);
				}
			}
		};

		collectChildIds(layers);

		// Return only items whose ID is not a child of any group
		return layers.filter(layer => !childIds.has(layer.id));
	}

	/**
	 * Returns true if the layer is effectively hidden: either it has hidden=true,
	 * or any of its ancestors has hidden=true (nested layer under a hidden group).
	 * @param layer - The layer to check
	 * @param allLayers - The full layer tree
	 */
	public static isLayerEffectivelyHidden(layer: LayerDto, allLayers: LayerDto[]): boolean {
		if (!layer) return false;
		if (layer.hidden === true) return true;

		let parent = LayersUtils.findLayerParent(allLayers, layer.id);
		while (parent) {
			if (parent.hidden === true) return true;
			parent = LayersUtils.findLayerParent(allLayers, parent.id);
		}
		return false;
	}

	/**
	 * Strips a layer down to its basic properties, removing any nested layers and complex information.
	 * @param layer
	 */
	public static stripLayerToBasics(layer: LayerDto): LayerDto {
		const propertiesToKeep = ['id', 'creativeUnitId', 'label', 'type', 'remoteId', 'purpose'];

		const strippedLayer = {} as LayerDto;
		for (const prop of propertiesToKeep) {
			if (layer[prop] !== undefined) {
				strippedLayer[prop] = layer[prop];
			}
		}

		return strippedLayer;
	}

	/**
	 * Gets the styles to apply the 'fit' sizing as requested on ticket#9055
	 * @param parentLayer
	 */
	public static getStyleOverridesForNestedLayer(parentLayer: LayerDto | undefined) {
		switch (parentLayer?.group?.layout?.mode) {
			case LayoutMode.FlexHorizontal:
				return { flex: '1', width: 'unset', height: '100%', position: 'relative' };
			case LayoutMode.FlexVertical:
				return { flex: '1', height: 'unset', width: '100%', position: 'relative' };
			case LayoutMode.Grid:
				return { flex: 'unset', width: '100%', height: '100%', position: 'relative' };
			default:
				return { position: 'absolute' };
		}
	}

	/**
	 * Helper method to find a layer within a creative unit's layers (including nested layers)
	 */
	public static findLayerInUnit(unit: CreativeUnitWithCustomizationsDto, layerId: string): LayerDto | null {
		if (!unit.layers) {
			return null;
		}

		const findLayer = (layers: LayerDto[]): LayerDto | null => {
			for (const layer of layers) {
				if (layer.id === layerId) {
					return layer;
				}
				if (layer.layers) {
					const found = findLayer(layer.layers);
					if (found) {
						return found;
					}
				}
			}
			return null;
		};

		return findLayer(unit.layers);
	}
}
